// src/components/BuildingViews/TanningStationView.js
import React, { useState } from 'react';
import styles from './TanningStationView.module.css';
import { ItemData } from '../../data/ItemData';
import { getItemImageSrc } from '../../utils/getItemImageSrc';

const TanningStationView = ({ playerData, onPlayerAction, onGoBack }) => {
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Get current crafting status
  const activeCrafting = playerData?.buildings?.tanningStation?.activeCrafting;

  const tanningRecipes = [
    {
      id: 'craftLeatherArmor',
      name: 'Craft Leather Armor',
      description: 'Create basic leather chest protection',
      input: { 'coarseLeather': 3, 'string': 2 },
      output: { 'leatherArmor': 1 },
      duration: 180, // 3 minutes
      icon: 'wearables/leatherArmor.png'
    },
    {
      id: 'craftLeatherBoots',
      name: 'Craft Leather Boots',
      description: 'Make sturdy leather footwear',
      input: { 'coarseLeather': 2, 'string': 1 },
      output: { 'leatherBoots': 1 },
      duration: 120, // 2 minutes
      icon: 'wearables/leatherBoots.png'
    },
    {
      id: 'craftLeatherCap',
      name: 'Craft Leather Cap',
      description: 'Fashion a simple leather helmet',
      input: { 'coarseLeather': 1, 'string': 1 },
      output: { 'leatherCap': 1 },
      duration: 90, // 1.5 minutes
      icon: 'wearables/leatherCap.png'
    },
    {
      id: 'craftLeatherPants',
      name: 'Craft Leather Pants',
      description: 'Make protective leather leg armor',
      input: { 'coarseLeather': 2, 'string': 2 },
      output: { 'leatherPants': 1 },
      duration: 150, // 2.5 minutes
      icon: 'wearables/leatherPants.png'
    },
    {
      id: 'craftLeatherBelt',
      name: 'Craft Leather Belt',
      description: 'Make a utility belt for carrying items',
      input: { 'coarseLeather': 1, 'string': 1 },
      output: { 'leatherBelt': 1 },
      duration: 60, // 1 minute
      icon: 'wearables/leatherBelt.png'
    },
    {
      id: 'processRawHide',
      name: 'Process Raw Hide',
      description: 'Turn animal hides into workable leather',
      input: { 'bearPelt': 1 },
      output: { 'coarseLeather': 3 },
      duration: 150, // 2.5 minutes
      icon: 'crafting/coarseLeather.png'
    }
  ];

  const canCraftRecipe = (recipe) => {
    for (const [itemKey, requiredAmount] of Object.entries(recipe.input)) {
      const available = playerData.inventory[itemKey] || 0;
      if (available < requiredAmount) {
        return false;
      }
    }
    return true;
  };

  const handleCraftClick = (recipe) => {
    if (!canCraftRecipe(recipe) || activeCrafting) {
      return;
    }

    setIsProcessing(true);
    setSelectedRecipe(recipe);
    
    onPlayerAction(recipe.id);
    
    setTimeout(() => {
      setIsProcessing(false);
      setSelectedRecipe(null);
    }, 1000);
  };

  const handleStopCrafting = () => {
    onPlayerAction('stopCrafting', { buildingKey: 'tanningStation' });
  };

  const handleToggleContinuous = () => {
    onPlayerAction('toggleContinuousCrafting', { buildingKey: 'tanningStation' });
  };

  const buildingLevel = playerData.buildings?.tanningStation?.upgradeLevel || 0;
  const efficiencyBonus = buildingLevel * 10;

  return (
    <div className={styles.tanningContainer}>
      {onGoBack && (
        <button 
          className={styles.backButton}
          onClick={onGoBack}
          title="Back to Home"
        >
          ✕
        </button>
      )}
      <div className={styles.tanningHeader}>
        <img src="/img/buildingItems/tanningStation.png" alt="Tanning Station" className={styles.tanningIcon} />
        <div className={styles.tanningInfo}>
          <h2>Leather Working</h2>
          <p>Level {buildingLevel} {efficiencyBonus > 0 && `(-${efficiencyBonus}% time)`}</p>
          <p className={styles.description}>
            Process hides and craft leather armor for protection
          </p>
        </div>
      </div>

      {activeCrafting && (
        <div className={styles.craftingProgress}>
          <h3>🦎 Currently Tanning</h3>
          <div className={styles.activeRecipe}>
            <div className={styles.recipeInfo}>
              <span className={styles.recipeName}>
                {activeCrafting.actionConfig.output ? 
                  Object.keys(activeCrafting.actionConfig.output).map(key => 
                    ItemData[key]?.name || key
                  ).join(', ') :
                  ItemData[activeCrafting.actionConfig.outputItemKey]?.name || activeCrafting.actionConfig.outputItemKey
                }
              </span>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${(activeCrafting.progress || 0) * 100}%` }}
                ></div>
              </div>
              <span className={styles.progressText}>
                {Math.floor((activeCrafting.progress || 0) * 100)}% Complete
              </span>
            </div>
            <div className={styles.craftingControls}>
              <button 
                onClick={handleToggleContinuous}
                className={`${styles.controlButton} ${activeCrafting.continuousCrafting ? styles.active : ''}`}
              >
                {activeCrafting.continuousCrafting ? '🔄 Continuous ON' : '🔄 Continuous OFF'}
              </button>
              <button 
                onClick={handleStopCrafting}
                className={`${styles.controlButton} ${styles.stopButton}`}
              >
                ⏹️ Stop
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.recipeGrid}>
        {tanningRecipes.map((recipe) => {
          const canCraft = canCraftRecipe(recipe);
          const isSelected = selectedRecipe?.id === recipe.id;
          
          return (
            <div 
              key={recipe.id} 
              className={`${styles.recipeCard} ${!canCraft || activeCrafting ? styles.disabled : ''} ${isSelected ? styles.selected : ''}`}
            >
              <div className={styles.recipeHeader}>
                <img 
                  src={getItemImageSrc(recipe.icon)} 
                  alt={recipe.name} 
                  className={styles.recipeIcon} 
                />
                <div className={styles.recipeInfo}>
                  <h3>{recipe.name}</h3>
                  <p>{recipe.description}</p>
                </div>
              </div>

              <div className={styles.recipeDetails}>
                <div className={styles.requirements}>
                  <h4>Required:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.input).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      const available = playerData.inventory[itemKey] || 0;
                      const hasEnough = available >= amount;
                      
                      return (
                        <div key={itemKey} className={`${styles.itemRequirement} ${!hasEnough ? styles.insufficient : ''}`}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>
                            {available}/{amount}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.output}>
                  <h4>Produces:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.output).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      
                      return (
                        <div key={itemKey} className={styles.itemOutput}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>x{amount}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.recipeStats}>
                  <div className={styles.duration}>
                    <span>⏱️ {Math.floor(recipe.duration * (1 - efficiencyBonus / 100))}s</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => handleCraftClick(recipe)}
                disabled={!canCraft || isProcessing || activeCrafting}
                className={`${styles.craftButton} ${isProcessing && isSelected ? styles.processing : ''}`}
              >
                {activeCrafting ? 'Tanning Station Busy' : (isProcessing && isSelected ? 'Working...' : 'Start Crafting')}
              </button>
            </div>
          );
        })}
      </div>

      <div className={styles.tanningTips}>
        <h4>🦎 Tanning Tips</h4>
        <ul>
          <li>Hunt animals to collect hides for processing</li>
          <li>Leather armor provides good protection without weight</li>
          <li>String is essential for binding leather pieces together</li>
        </ul>
      </div>
    </div>
  );
};

export default TanningStationView; 